// ============================================================================
// STAKEHOLDER LOCATIONS TAB
// Full CRUD for contact locations inside WorkItemDetailModal.
// Inline add/edit form with GoogleMapsPicker, SA province detection,
// billing details and all fields from the legacy LocationModal.
// Date: March 2026
// ============================================================================

'use client';

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  MapPin,
  Search,
  Plus,
  Navigation,
  CheckCircle2,
  XCircle,
  Star,
  Globe,
  Map,
  Truck,
  CreditCard,
  Save,
  Loader2,
  Trash2,
  ChevronLeft,
  Edit,
  LocateFixed,
} from 'lucide-react';
import type { WorkItem } from '../UniversalWorkItems';
import GoogleMapsPicker from '@/components/maps/GoogleMapsPicker';
import { useContactConfig } from '@/hooks/useContactConfig';
import { authService } from '@/lib/authService';
import { notify, confirm } from '@/lib/notifications';

const getAuthToken = () => authService.getTokens()?.access_token || null;

// ============================================================================
// Types
// ============================================================================

interface StakeholderLocationsTabProps {
  workItem: WorkItem;
  isDark: boolean;
  onRefresh?: () => void;
}

interface StakeholderLocation {
  id: number;
  contact_id: number;
  contact_person_id?: number | null;
  location_name: string;
  location_type?: string | null;
  address_line1?: string | null;
  address_line2?: string | null;
  city?: string | null;
  state?: string | null;
  postal_code?: string | null;
  country?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  radius_meters?: number;
  is_primary?: boolean;
  is_billing?: boolean;
  is_shipping?: boolean;
  is_active?: boolean;
  is_company_location?: boolean;
  no_appointment_required?: boolean;
  notes?: string | null;
  billing_entity_name?: string | null;
  billing_registration_number?: string | null;
  billing_tax_number?: string | null;
  billing_email?: string | null;
  billing_phone?: string | null;
  billing_attention?: string | null;
  location_code?: string | null;
  cost_centre?: string | null;
  contact_name?: string | null;
  contact_company_name?: string | null;
  contact_person?: string | null;
  whatsapp_group_jid?: string | null;
}

interface LocationFormData {
  contact_id: number;
  contact_person_id: number | null;
  location_name: string;
  location_type: string;
  address_line1: string;
  address_line2: string;
  city: string;
  state: string;
  postal_code: string;
  country: string;
  latitude: number | null;
  longitude: number | null;
  radius_meters: number;
  is_primary: boolean;
  is_billing: boolean;
  is_shipping: boolean;
  is_active: boolean;
  is_company_location: boolean;
  no_appointment_required: boolean;
  notes: string;
  billing_entity_name: string;
  billing_registration_number: string;
  billing_tax_number: string;
  billing_email: string;
  billing_phone: string;
  billing_attention: string;
  location_code: string;
  cost_centre: string;
  whatsapp_group_jid: string;
}

function toFiniteNumber(value: unknown): number | null {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) {
      return null;
    }

    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
}

function normalizeStakeholderLocation<T extends StakeholderLocation>(location: T): T {
  return {
    ...location,
    latitude: toFiniteNumber(location.latitude),
    longitude: toFiniteNumber(location.longitude),
    radius_meters: toFiniteNumber(location.radius_meters) ?? 100,
  };
}

function hasCoordinates(location?: Partial<StakeholderLocation> | null): boolean {
  return toFiniteNumber(location?.latitude) !== null && toFiniteNumber(location?.longitude) !== null;
}

function formatCoordinates(latitude: unknown, longitude: unknown): string | null {
  const lat = toFiniteNumber(latitude);
  const lng = toFiniteNumber(longitude);

  if (lat === null || lng === null) {
    return null;
  }

  return `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
}

// ============================================================================
// SA Province detection
// ============================================================================

function detectSAProvince(postalCode: string, city: string): string {
  if (postalCode) {
    const pc = parseInt(postalCode, 10);
    if (!isNaN(pc)) {
      if ((pc >= 0 && pc <= 299) || (pc >= 1000 && pc <= 2199)) return 'Gauteng';
      if (pc >= 300 && pc <= 499) return 'North West';
      if ((pc >= 500 && pc <= 699) || (pc >= 9300 && pc <= 9999)) return 'Free State';
      if ((pc >= 700 && pc <= 999) || (pc >= 8300 && pc <= 8999)) return 'Northern Cape';
      if (pc >= 2500 && pc <= 2999) return 'Mpumalanga';
      if ((pc >= 3000 && pc <= 4099) || (pc >= 4100 && pc <= 4499)) return 'KwaZulu-Natal';
      if ((pc >= 4500 && pc <= 5299) || (pc >= 5800 && pc <= 6499)) return 'Eastern Cape';
      if ((pc >= 6500 && pc <= 6999) || (pc >= 7000 && pc <= 8299)) return 'Western Cape';
    }
  }
  const cl = (city || '').toLowerCase();
  if (['johannesburg','pretoria','centurion','sandton','midrand','randburg','soweto','roodepoort','kempton park','benoni','boksburg','germiston'].some(c => cl.includes(c))) return 'Gauteng';
  if (['cape town','stellenbosch','paarl','george','knysna','mossel bay'].some(c => cl.includes(c))) return 'Western Cape';
  if (['durban','pietermaritzburg','richards bay','newcastle'].some(c => cl.includes(c))) return 'KwaZulu-Natal';
  if (['port elizabeth','east london','gqeberha','mthatha'].some(c => cl.includes(c))) return 'Eastern Cape';
  if (['bloemfontein','welkom'].some(c => cl.includes(c))) return 'Free State';
  if (['polokwane','tzaneen','louis trichardt'].some(c => cl.includes(c))) return 'Limpopo';
  if (['nelspruit','mbombela','witbank','emalahleni','secunda'].some(c => cl.includes(c))) return 'Mpumalanga';
  if (['rustenburg','mahikeng','klerksdorp','potchefstroom'].some(c => cl.includes(c))) return 'North West';
  if (['kimberley','upington'].some(c => cl.includes(c))) return 'Northern Cape';
  return '';
}

function buildAddress(loc: StakeholderLocation): string {
  return [loc.address_line1, loc.address_line2, loc.city, loc.state, loc.postal_code, loc.country]
    .filter((p): p is string => typeof p === 'string' && p.trim().length > 0).join(', ');
}

const LOCATION_TYPE_COLORS: Record<string, { light: string; dark: string }> = {
  office: { light: 'bg-blue-100 text-blue-700', dark: 'bg-blue-900/30 text-blue-300' },
  warehouse: { light: 'bg-amber-100 text-amber-700', dark: 'bg-amber-900/30 text-amber-300' },
  billing: { light: 'bg-green-100 text-green-700', dark: 'bg-green-900/30 text-green-300' },
  shipping: { light: 'bg-purple-100 text-purple-700', dark: 'bg-purple-900/30 text-purple-300' },
  service_location: { light: 'bg-teal-100 text-teal-700', dark: 'bg-teal-900/30 text-teal-300' },
  job_site: { light: 'bg-orange-100 text-orange-700', dark: 'bg-orange-900/30 text-orange-300' },
};

const EMPTY_FORM: LocationFormData = {
  contact_id: 0, contact_person_id: null,
  location_name: '', location_type: '', address_line1: '', address_line2: '',
  city: '', state: '', postal_code: '', country: 'South Africa',
  latitude: null, longitude: null, radius_meters: 100,
  is_primary: false, is_billing: false, is_shipping: false, is_active: true,
  is_company_location: false, no_appointment_required: false, notes: '',
  billing_entity_name: '', billing_registration_number: '', billing_tax_number: '',
  billing_email: '', billing_phone: '', billing_attention: '',
  location_code: '', cost_centre: '',
  whatsapp_group_jid: '',
};

// ============================================================================
// Component
// ============================================================================

export default function StakeholderLocationsTab({ workItem, isDark, onRefresh }: StakeholderLocationsTabProps) {
  const { config } = useContactConfig();
  const [searchQuery, setSearchQuery] = useState('');
  const [editingLocation, setEditingLocation] = useState<StakeholderLocation | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [loading, setLoading] = useState(false);
  const [geocoding, setGeocoding] = useState(false);
  const [currentUserRole, setCurrentUserRole] = useState('user');
  const [contactPeople, setContactPeople] = useState<Array<{ id: number; first_name: string; last_name: string }>>([]);
  const [formData, setFormData] = useState<LocationFormData>({ ...EMPTY_FORM });
  const [selectedLocationId, setSelectedLocationId] = useState<number | null>(null);

  const cf = useMemo(() => {
    if (!workItem.custom_fields) return {} as Record<string, any>;
    return typeof workItem.custom_fields === 'string' ? JSON.parse(workItem.custom_fields) : workItem.custom_fields;
  }, [workItem.custom_fields]);

  const locations = useMemo<StakeholderLocation[]>(() => (
    Array.isArray(cf.related_locations)
      ? (cf.related_locations as StakeholderLocation[]).map((location) => normalizeStakeholderLocation(location))
      : []
  ), [cf.related_locations]);
  const contactId = cf.entity_id || workItem.contact_id;
  const showForm = isAdding || !!editingLocation;

  const locationTypes = useMemo(() => (config.location_types && config.location_types.length > 0) ? config.location_types : ['office', 'warehouse', 'billing', 'shipping', 'service_location', 'job_site'], [config.location_types]);

  // Load user role
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch('/api/v1/auth/me', { headers: { Authorization: `Bearer ${getAuthToken()}` } });
        if (res.ok) { const d = await res.json(); if (d.success && d.user) setCurrentUserRole(d.user.role || d.user.system_role || 'user'); }
      } catch { /* ignore */ }
    })();
  }, []);

  // Load contact people when form opens (for person assignment)
  useEffect(() => {
    if (!showForm || !contactId || !config.location_require_person) return;
    (async () => {
      try {
        const { fetchContactPeople } = await import('@/lib/contacts/fetchContactPeople');
        const people = await fetchContactPeople<{ id: number; first_name: string; last_name: string }>({
          contactId,
          token: getAuthToken(),
        });
        setContactPeople(people || []);
      } catch { /* ignore */ }
    })();
  }, [showForm, contactId, config.location_require_person]);

  const filteredLocations = useMemo(() => {
    if (!searchQuery.trim()) return locations;
    const q = searchQuery.toLowerCase();
    return locations.filter((loc) => {
      return loc.location_name.toLowerCase().includes(q) || buildAddress(loc).toLowerCase().includes(q) || (loc.location_type || '').toLowerCase().includes(q) || (loc.city || '').toLowerCase().includes(q);
    });
  }, [locations, searchQuery]);

  const mapGeofences = useMemo(() => (
    locations
      .filter((location) => hasCoordinates(location))
      .map((location) => ({
        id: location.id,
        name: location.location_name,
        latitude: toFiniteNumber(location.latitude) ?? undefined,
        longitude: toFiniteNumber(location.longitude) ?? undefined,
        radius: toFiniteNumber(location.radius_meters) ?? 100,
        location_type: location.location_type || 'office'
      }))
  ), [locations]);
  const mapInitial = useMemo(() => {
    const t = selectedLocationId ? locations.find(l => l.id === selectedLocationId) : locations.find(l => hasCoordinates(l));
    if (t && hasCoordinates(t)) return { lat: String(t.latitude), lng: String(t.longitude) };
    return { lat: '-26.2041', lng: '28.0473' };
  }, [locations, selectedLocationId]);

  const resetForm = useCallback((loc?: StakeholderLocation | null) => {
    if (loc) {
      setFormData({
        contact_id: loc.contact_id || contactId,
        contact_person_id: loc.contact_person_id || null,
        location_name: loc.location_name || '',
        location_type: loc.location_type || '',
        address_line1: loc.address_line1 || '',
        address_line2: loc.address_line2 || '',
        city: loc.city || '',
        state: loc.state || '',
        postal_code: loc.postal_code || '',
        country: loc.country || 'South Africa',
        latitude: toFiniteNumber(loc.latitude),
        longitude: toFiniteNumber(loc.longitude),
        radius_meters: toFiniteNumber(loc.radius_meters) ?? 100,
        is_primary: loc.is_primary || false,
        is_billing: loc.is_billing || false,
        is_shipping: loc.is_shipping || false,
        is_active: loc.is_active ?? true,
        is_company_location: loc.is_company_location || false,
        no_appointment_required: loc.no_appointment_required || false,
        notes: loc.notes || '',
        billing_entity_name: loc.billing_entity_name || '',
        billing_registration_number: loc.billing_registration_number || '',
        billing_tax_number: loc.billing_tax_number || '',
        billing_email: loc.billing_email || '',
        billing_phone: loc.billing_phone || '',
        billing_attention: loc.billing_attention || '',
        location_code: loc.location_code || '',
        cost_centre: loc.cost_centre || '',
        whatsapp_group_jid: loc.whatsapp_group_jid || '',
      });
    } else {
      setFormData({ ...EMPTY_FORM, contact_id: contactId });
    }
  }, [contactId]);

  const handleAdd = () => { setEditingLocation(null); resetForm(); setIsAdding(true); };
  const handleEdit = (loc: StakeholderLocation) => { setIsAdding(false); setEditingLocation(loc); resetForm(loc); };
  const handleCancel = () => { setIsAdding(false); setEditingLocation(null); };

  const handleLocationSelect = (latValue: string, lngValue: string, address?: string) => {
    const lat = parseFloat(latValue);
    const lng = parseFloat(lngValue);
    const parsed = { street: '', city: '', state: '', postal_code: '', country: '' };
    if (address) {
      const parts = address.split(',').map(p => p.trim());
      if (parts.length >= 5) { parsed.street = parts[0]; parsed.city = parts[2]; parsed.postal_code = parts[3]; parsed.country = parts[4] || ''; }
      else if (parts.length >= 4) { parsed.street = parts[0]; parsed.city = parts[1]; parsed.postal_code = parts[2]; parsed.country = parts[3] || ''; }
      else if (parts.length >= 2) { parsed.street = parts[0]; parsed.city = parts[1]; }
      if (parsed.country === 'South Africa' || address.includes('South Africa')) {
        parsed.state = detectSAProvince(parsed.postal_code, parsed.city);
      }
    }
    setFormData(prev => ({ ...prev, latitude: lat, longitude: lng, address_line1: parsed.street || address || prev.address_line1, city: parsed.city || prev.city, state: parsed.state || prev.state, postal_code: parsed.postal_code || prev.postal_code, country: parsed.country || prev.country }));
  };

  const geocodeAddress = async () => {
    const address = [formData.address_line1, formData.city, formData.state, formData.postal_code, formData.country].filter(Boolean).join(', ');
    if (!address || address.length < 5) { notify.error('Enter an address to geocode'); return; }
    setGeocoding(true);
    try {
      const res = await fetch(`/api/v1/geocode?address=${encodeURIComponent(address)}`);
      if (res.ok) {
        const d = await res.json();
        const lat = toFiniteNumber(d.lat);
        const lng = toFiniteNumber(d.lng);
        if (d.success && lat !== null && lng !== null) { setFormData(p => ({ ...p, latitude: lat, longitude: lng })); notify.success('Coordinates found'); setGeocoding(false); return; }
      }
      // Fallback: Nominatim
      const nr = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(address)}`, { headers: { 'User-Agent': 'TotalAccess/1.0' } });
      if (nr.ok) { const r = await nr.json(); if (r.length) { setFormData(p => ({ ...p, latitude: parseFloat(r[0].lat), longitude: parseFloat(r[0].lon) })); notify.success('Coordinates found'); } else notify.error('Could not geocode. Use the map picker.'); }
    } catch { notify.error('Geocode failed. Use the map picker.'); } finally { setGeocoding(false); }
  };

  const handleSave = async () => {
    if (!formData.location_name) { notify.error('Location name is required'); return; }
    if (!formData.location_type) { notify.error('Location type is required'); return; }
    if (config.location_require_person && !formData.contact_person_id) { notify.error('Please select a contact person'); return; }
    if (formData.latitude == null || formData.longitude == null) { notify.error('GPS coordinates required. Use the map to pin the location.'); return; }

    const locId = editingLocation?.id;
    const cid = formData.contact_id || contactId;
    setLoading(true);
    try {
      const url = locId ? `/api/v1/contacts/${cid}/locations/${locId}` : `/api/v1/contacts/${cid}/locations`;
      const apiPayload = {
        name: formData.location_name, description: formData.notes,
        address: [formData.address_line1, formData.address_line2, formData.city, formData.state, formData.postal_code, formData.country].filter(Boolean).join(', '),
        center_latitude: formData.latitude, center_longitude: formData.longitude,
        gps_radius_meters: formData.radius_meters || 100, category: formData.location_type,
        is_active: formData.is_active, contact_id: cid, contact_person_id: formData.contact_person_id,
        // Raw fields for backwards compat
        location_name: formData.location_name, location_type: formData.location_type,
        address_line1: formData.address_line1, address_line2: formData.address_line2,
        city: formData.city, state: formData.state, postal_code: formData.postal_code,
        country: formData.country, latitude: formData.latitude, longitude: formData.longitude,
        radius_meters: formData.radius_meters, is_primary: formData.is_primary,
        is_billing: formData.is_billing, is_shipping: formData.is_shipping,
        no_appointment_required: formData.no_appointment_required, notes: formData.notes,
        location_code: formData.location_code, cost_centre: formData.cost_centre,
        billing_entity_name: formData.billing_entity_name, billing_registration_number: formData.billing_registration_number,
        billing_tax_number: formData.billing_tax_number, billing_email: formData.billing_email,
        billing_phone: formData.billing_phone, billing_attention: formData.billing_attention,
        whatsapp_group_jid: formData.whatsapp_group_jid || null,
      };
      const res = await fetch(url, {
        method: locId ? 'PUT' : 'POST',
        headers: { Authorization: `Bearer ${getAuthToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(apiPayload),
      });
      if (res.ok) { notify.success(locId ? 'Location updated' : 'Location created'); handleCancel(); onRefresh?.(); }
      else { const d = await res.json(); notify.error(d.error || 'Failed to save location'); }
    } catch { notify.error('Failed to save location'); } finally { setLoading(false); }
  };

  const handleDelete = async () => {
    if (!editingLocation?.id) return;
    if (currentUserRole !== 'admin' && currentUserRole !== 'owner') { notify.error('Only admins and owners can delete locations'); return; }
    await confirm({
      title: 'Delete Location', message: `Delete "${editingLocation.location_name}"? This cannot be undone.`,
      confirmText: 'Delete', variant: 'danger',
      onConfirm: async () => {
        try {
          const res = await fetch(`/api/v1/contacts/locations/${editingLocation.id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${getAuthToken()}` } });
          if (res.ok) { notify.success('Location deleted'); handleCancel(); onRefresh?.(); }
          else { const d = await res.json(); notify.error(d.error || 'Failed to delete'); }
        } catch { notify.error('Failed to delete location'); }
      },
    });
  };

  const cardBg = isDark ? 'bg-gray-800/60 border-gray-700' : 'bg-white border-gray-200';
  const inputClass = `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm ${isDark ? 'border-gray-600 bg-gray-800 text-white' : 'border-gray-300 bg-white text-gray-900'}`;
  const labelClass = `block text-sm font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`;

  // ---------------------------------------------------------------------------
  // FORM VIEW
  // ---------------------------------------------------------------------------
  if (showForm) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <button onClick={handleCancel} className={`flex items-center gap-1 text-sm font-medium ${isDark ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'}`}>
            <ChevronLeft className="w-4 h-4" /> Back to list
          </button>
          <h3 className={`text-lg font-semibold ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>{editingLocation?.id ? 'Edit Location' : 'New Location'}</h3>
        </div>

        <div className={`rounded-xl border p-5 space-y-5 ${cardBg}`}>
          {/* Map */}
          <div className={`h-[280px] rounded-lg border overflow-hidden ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
            <GoogleMapsPicker
              initialLat={formData.latitude ? String(formData.latitude) : mapInitial.lat}
              initialLng={formData.longitude ? String(formData.longitude) : mapInitial.lng}
              onLocationSelect={handleLocationSelect}
            />
          </div>

          {/* Name + Type */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div><label className={labelClass}>Location Name *</label><input type="text" value={formData.location_name} onChange={e => setFormData(p => ({ ...p, location_name: e.target.value }))} className={inputClass} placeholder="e.g. Head Office" /></div>
            <div>
              <label className={labelClass}>Location Type *</label>
              <select value={formData.location_type} onChange={e => setFormData(p => ({ ...p, location_type: e.target.value }))} className={inputClass}>
                <option value="">Select type...</option>
                {locationTypes.map(t => <option key={t} value={t}>{t.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</option>)}
              </select>
            </div>
          </div>

          {/* Person assignment */}
          {config.location_require_person && (
            <div><label className={labelClass}>Contact Person *</label>
              <select value={formData.contact_person_id || ''} onChange={e => setFormData(p => ({ ...p, contact_person_id: e.target.value ? parseInt(e.target.value) : null }))} className={inputClass}>
                <option value="">Select person...</option>
                {contactPeople.map(p => <option key={p.id} value={p.id}>{p.first_name} {p.last_name}</option>)}
              </select>
            </div>
          )}

          {/* Address */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2"><label className={labelClass}>Street Address</label><input type="text" value={formData.address_line1} onChange={e => setFormData(p => ({ ...p, address_line1: e.target.value }))} className={inputClass} /></div>
            <div className="sm:col-span-2"><label className={labelClass}>Address Line 2</label><input type="text" value={formData.address_line2} onChange={e => setFormData(p => ({ ...p, address_line2: e.target.value }))} className={inputClass} /></div>
            <div><label className={labelClass}>City</label><input type="text" value={formData.city} onChange={e => setFormData(p => ({ ...p, city: e.target.value }))} className={inputClass} /></div>
            <div><label className={labelClass}>Province / State</label><input type="text" value={formData.state} onChange={e => setFormData(p => ({ ...p, state: e.target.value }))} className={inputClass} /></div>
            <div><label className={labelClass}>Postal Code</label><input type="text" value={formData.postal_code} onChange={e => setFormData(p => ({ ...p, postal_code: e.target.value }))} className={inputClass} /></div>
            <div><label className={labelClass}>Country</label><input type="text" value={formData.country} onChange={e => setFormData(p => ({ ...p, country: e.target.value }))} className={inputClass} /></div>
          </div>

          {/* Geocode button */}
          <div className="flex items-center gap-3">
            <button onClick={geocodeAddress} disabled={geocoding} className={`flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg font-medium ${isDark ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
              {geocoding ? <Loader2 className="w-4 h-4 animate-spin" /> : <LocateFixed className="w-4 h-4" />} Geocode Address
            </button>
            {formatCoordinates(formData.latitude, formData.longitude) && (
              <span className={`text-xs ${isDark ? 'text-green-400' : 'text-green-600'}`}>
                <Globe className="w-3.5 h-3.5 inline mr-1" />{formatCoordinates(formData.latitude, formData.longitude)}
              </span>
            )}
          </div>

          {/* Radius */}
          <div className="w-48"><label className={labelClass}>Geofence Radius (m)</label><input type="number" value={formData.radius_meters} onChange={e => setFormData(p => ({ ...p, radius_meters: parseInt(e.target.value) || 100 }))} className={inputClass} min={10} max={5000} /></div>

          {/* Location code / Cost centre */}
          {(config.location_code_visible || config.location_cost_centre_visible) && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {config.location_code_visible && <div><label className={labelClass}>Location Code</label><input type="text" value={formData.location_code} onChange={e => setFormData(p => ({ ...p, location_code: e.target.value }))} className={inputClass} /></div>}
              {config.location_cost_centre_visible && <div><label className={labelClass}>Cost Centre</label><input type="text" value={formData.cost_centre} onChange={e => setFormData(p => ({ ...p, cost_centre: e.target.value }))} className={inputClass} /></div>}
            </div>
          )}

          {/* Checkboxes */}
          <div className="flex flex-wrap gap-x-6 gap-y-2">
            {[
              { key: 'is_primary', label: 'Primary' },
              { key: 'is_billing', label: 'Billing' },
              { key: 'is_shipping', label: 'Shipping' },
              { key: 'is_active', label: 'Active' },
              { key: 'is_company_location', label: 'Company Location' },
              { key: 'no_appointment_required', label: 'No Appointment Required' },
            ].map(({ key, label }) => (
              <label key={key} className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={(formData as any)[key] || false} onChange={e => setFormData(p => ({ ...p, [key]: e.target.checked }))} className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{label}</span>
              </label>
            ))}
          </div>

          {/* Billing Details */}
          {config.location_show_billing && (
            <div className={`rounded-lg border p-4 space-y-4 ${isDark ? 'border-gray-700 bg-gray-800/40' : 'border-gray-200 bg-gray-50'}`}>
              <h4 className={`text-sm font-semibold ${isDark ? 'text-gray-200' : 'text-gray-800'}`}><CreditCard className="w-4 h-4 inline mr-1.5" />Billing Details</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><label className={labelClass}>Entity Name</label><input type="text" value={formData.billing_entity_name} onChange={e => setFormData(p => ({ ...p, billing_entity_name: e.target.value }))} className={inputClass} /></div>
                <div><label className={labelClass}>Registration No.</label><input type="text" value={formData.billing_registration_number} onChange={e => setFormData(p => ({ ...p, billing_registration_number: e.target.value }))} className={inputClass} /></div>
                <div><label className={labelClass}>Tax Number</label><input type="text" value={formData.billing_tax_number} onChange={e => setFormData(p => ({ ...p, billing_tax_number: e.target.value }))} className={inputClass} /></div>
                <div><label className={labelClass}>Billing Email</label><input type="email" value={formData.billing_email} onChange={e => setFormData(p => ({ ...p, billing_email: e.target.value }))} className={inputClass} /></div>
                <div><label className={labelClass}>Billing Phone</label><input type="text" value={formData.billing_phone} onChange={e => setFormData(p => ({ ...p, billing_phone: e.target.value }))} className={inputClass} /></div>
                <div><label className={labelClass}>Attention</label><input type="text" value={formData.billing_attention} onChange={e => setFormData(p => ({ ...p, billing_attention: e.target.value }))} className={inputClass} /></div>
              </div>
            </div>
          )}

          {/* Notes */}
          <div><label className={labelClass}>Notes</label><textarea value={formData.notes} onChange={e => setFormData(p => ({ ...p, notes: e.target.value }))} rows={2} className={inputClass} /></div>

          {/* WhatsApp Group for Site Notifications */}
          <div className={`rounded-lg border p-4 space-y-3 ${isDark ? 'bg-gray-800/50 border-gray-700' : 'bg-gray-50/70 border-gray-200'}`}>
            <div className="flex items-start gap-2">
              <div className={`rounded-lg p-1.5 ${isDark ? 'bg-teal-900/30' : 'bg-teal-100'}`}>
                <Navigation className={`w-4 h-4 ${isDark ? 'text-teal-300' : 'text-teal-600'}`} />
              </div>
              <div>
                <p className={`text-sm font-medium ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>WhatsApp Group for Site Notifications</p>
                <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  If set, on-route and on-site WhatsApp notifications for appointments at this location will be sent to this group instead of the contact person's direct WhatsApp.
                </p>
              </div>
            </div>
            <div>
              <label className={labelClass}>WhatsApp Group JID</label>
              <input
                type="text"
                value={formData.whatsapp_group_jid}
                onChange={e => setFormData(p => ({ ...p, whatsapp_group_jid: e.target.value }))}
                className={inputClass}
                placeholder="e.g. 1234567890-1234567890@g.us — leave blank to use contact person's WhatsApp"
              />
              <p className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                When a technician triggers on-route or on-site notifications, the system checks this location's group first. If empty, it falls back to the contact person's WhatsApp setting.
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between pt-2 border-t border-gray-200 dark:border-gray-700">
            {editingLocation?.id ? (
              <button onClick={handleDelete} className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg"><Trash2 className="w-4 h-4" /> Delete</button>
            ) : <div />}
            <div className="flex gap-3">
              <button onClick={handleCancel} className={`px-4 py-2 text-sm font-medium rounded-lg ${isDark ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-100'}`}>Cancel</button>
              <button onClick={handleSave} disabled={loading} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {editingLocation?.id ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // LIST VIEW
  // ---------------------------------------------------------------------------
  return (
    <div className="space-y-4" data-tour-id="stakeholder-locations-tab">
      {/* Map overview */}
      {mapGeofences.length > 0 && (
        <div className={`h-[240px] rounded-xl border overflow-hidden ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
          <GoogleMapsPicker initialLat={mapInitial.lat} initialLng={mapInitial.lng} onLocationSelect={() => {}} existingGeofences={mapGeofences} focusedGeofenceId={selectedLocationId ?? undefined} showSearch={false} />
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <MapPin className={`w-5 h-5 ${isDark ? 'text-amber-400' : 'text-amber-600'}`} />
          <h3 className={`text-lg font-semibold ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>{config.locations_label || 'Locations'}</h3>
          <span className={`text-sm px-2 py-0.5 rounded-full ${isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>{locations.length}</span>
        </div>
        <button onClick={handleAdd} data-tour-id="stakeholder-locations-add" className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg"><Plus className="w-4 h-4" /> Add Location</button>
      </div>

      {locations.length > 3 && (
        <div className="relative">
          <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
          <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search by name, address, city, type..." data-tour-id="stakeholder-locations-search" className={`w-full pl-9 pr-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500' : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400'}`} />
        </div>
      )}

      {filteredLocations.length === 0 ? (
        <div className={`text-center py-12 rounded-xl border ${isDark ? 'bg-gray-800/30 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
          <MapPin className={`w-10 h-10 mx-auto mb-3 ${isDark ? 'text-gray-600' : 'text-gray-300'}`} />
          <p className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{searchQuery ? 'No locations match your search' : 'No locations added yet'}</p>
          {!searchQuery && <button onClick={handleAdd} className="mt-3 text-sm font-medium text-blue-600 hover:text-blue-700">Add the first location</button>}
        </div>
      ) : (
        <div className="space-y-2" data-tour-id="stakeholder-locations-list">
          {filteredLocations.map(loc => {
            const address = buildAddress(loc);
            const isActive = loc.is_active !== false;
            const tc = LOCATION_TYPE_COLORS[loc.location_type || ''];
            const isSelected = selectedLocationId === loc.id;
            const hasCoords = hasCoordinates(loc) && Number(loc.latitude) !== 0;

            return (
              <div key={loc.id} onClick={() => { setSelectedLocationId(loc.id); handleEdit(loc); }} className={`rounded-xl border p-4 cursor-pointer hover:ring-2 hover:ring-blue-500/40 transition-shadow ${isSelected ? (isDark ? 'bg-blue-900/20 border-blue-700' : 'bg-blue-50 border-blue-300') : cardBg}`}>
                <div className="flex items-start gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${isDark ? 'bg-amber-900/30 text-amber-300' : 'bg-amber-100 text-amber-600'}`}><MapPin className="w-5 h-5" /></div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`font-medium text-sm ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>{loc.location_name}</span>
                      {loc.location_type && <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold ${tc ? (isDark ? tc.dark : tc.light) : (isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600')}`}>{loc.location_type.replace(/_/g, ' ')}</span>}
                      {loc.is_primary && <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${isDark ? 'bg-amber-900/30 text-amber-300' : 'bg-amber-100 text-amber-700'}`}><Star className="w-3 h-3" /> Primary</span>}
                      <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${isActive ? (isDark ? 'bg-green-900/30 text-green-300' : 'bg-green-100 text-green-700') : (isDark ? 'bg-red-900/30 text-red-300' : 'bg-red-100 text-red-700')}`}>
                        {isActive ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />} {isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    {address && <div className="flex items-start gap-1.5 mt-1"><Navigation className={`w-3.5 h-3.5 flex-shrink-0 mt-0.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} /><span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{address}</span></div>}
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5">
                      {hasCoords && <span className={`inline-flex items-center gap-1 text-xs ${isDark ? 'text-green-400' : 'text-green-600'}`}><Globe className="w-3.5 h-3.5" />{formatCoordinates(loc.latitude, loc.longitude)}</span>}
                      {!hasCoords && <span className={`inline-flex items-center gap-1 text-xs ${isDark ? 'text-amber-400' : 'text-amber-600'}`}><Map className="w-3.5 h-3.5" /> No coordinates</span>}
                      {loc.is_billing && <span className={`inline-flex items-center gap-1 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}><CreditCard className="w-3.5 h-3.5" /> Billing</span>}
                      {loc.is_shipping && <span className={`inline-flex items-center gap-1 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}><Truck className="w-3.5 h-3.5" /> Shipping</span>}
                      {loc.whatsapp_group_jid && <span className={`inline-flex items-center gap-1 text-xs ${isDark ? 'text-teal-400' : 'text-teal-600'}`}><Navigation className="w-3.5 h-3.5" /> WA Group</span>}
                    </div>
                  </div>
                  <Edit className={`w-4 h-4 flex-shrink-0 ${isDark ? 'text-gray-600' : 'text-gray-300'}`} />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}